import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';

type ImportantStatement = {
  id: string;
  body: string;
  label?: string;
};

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [importantStatements, setImportantStatements] = useState<ImportantStatement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch important statements from database on mount
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data, error } = await supabase
          .from('terms_content')
          .select('item_id, body, label')
          .eq('audience', 'guide')
          .eq('item_type', 'important')
          .order('sort_order', { ascending: true });

        if (error) {
          console.error('Error fetching terms:', error);
          setError('Failed to load terms of service. Please refresh the page.');
          return;
        }

        if (data) {
          const statements: ImportantStatement[] = data.map((item) => ({
            id: item.item_id,
            body: item.body,
            ...(item.label && { label: item.label }),
          }));
          setImportantStatements(statements);
        }
      } catch (err) {
        console.error('Unexpected error fetching terms:', err);
        setError('An unexpected error occurred. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTerms();
  }, []);

  const handleAccept = useCallback(async () => {
    try {
      const supabase = getSupabaseClient();

      // Update the user's profile to mark terms as accepted
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not found');
      }

      const { error } = await supabase
        .from('profiles')
        .update({ terms_accepted: true })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating terms acceptance:', error);
        throw error;
      }

      setAccepted(true);
      // Navigate to dashboard after a brief delay to show acceptance
      setTimeout(() => {
        navigate('/start');
      }, 500);
    } catch (error) {
      console.error('Failed to accept terms:', error);
      alert('Failed to accept terms. Please try again.');
    }
  }, [navigate]);

  const handleViewFullTerms = () => {
    navigate('/terms/full');
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FFFCEF]">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C2731] border-r-transparent" />
          <p className="text-sm text-[#2C2731]/70">Loading terms...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FFFCEF]">
        <div className="max-w-md rounded-2xl border border-red-300 bg-red-50 p-6 text-center">
          <p className="mb-2 text-sm font-semibold text-red-900">Error Loading Terms</p>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-14 flex-shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.png" alt="Guides Collective" className="h-7 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        <main className="flex flex-1 min-h-0 flex-col overflow-hidden px-6 pb-6 pt-6">
          <div className="flex flex-col gap-2 mb-4 flex-shrink-0">
            <h1 className="font-recoleta text-[24px] font-normal leading-[1.1] text-[#304031]">Guide Terms of Service</h1>
            <p className="text-[14px] leading-relaxed text-[#214D3C]">
              Please review the important statements below before accepting.
            </p>
            <button
              onClick={handleViewFullTerms}
              className="text-left text-sm text-[#214D3C] underline hover:text-[#304031] transition-colors"
            >
              View full terms of service →
            </button>
          </div>

          {/* Statements list */}
          <div className="flex-1 min-h-0 overflow-y-auto rounded-3xl border border-[#304031]/15 bg-white/70 p-4 shadow-inner">
            <div className="space-y-4">
              {importantStatements.map((statement) => (
                <div
                  key={statement.id}
                  className="rounded-2xl border border-[#214D3C]/20 bg-white p-5 shadow-sm"
                >
                  {statement.label && (
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#214D3C]/70 mb-2">
                      {statement.label}
                    </p>
                  )}
                  <p className="text-[15px] leading-relaxed text-[#2C2731]">
                    {statement.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Accept section */}
          <div className="mt-4 flex-shrink-0">
            <button
              type="button"
              disabled={accepted}
              onClick={handleAccept}
              className={`group inline-flex w-full lg:w-auto items-center justify-between lg:justify-center gap-3 lg:gap-2 rounded-full border border-[#304031] px-6 py-4 lg:py-3 lg:px-8 text-lg lg:text-base font-medium lg:font-semibold shadow-[0_16px_36px_-20px_rgba(48,64,49,0.55)] lg:shadow-none transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]
                ${
                  !accepted
                    ? 'bg-[#304031] text-[#FFFCEF] hover:-translate-y-0.5 lg:hover:translate-y-0 lg:hover:bg-[#304031]/90'
                    : 'cursor-not-allowed bg-transparent text-[#304031]/50'
                }
              `}
            >
              <span className="invisible w-5 text-xl leading-none lg:hidden">{'\u2190'}</span>
              <span className="font-medium lg:font-semibold">{accepted ? 'Accepted' : 'I accept the terms of service'}</span>
              <span className="w-5 lg:w-auto text-xl lg:text-base leading-none">{'\u2192'}</span>
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
